import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

import { PADX } from "../constants";
import { type CategoryTile } from "../types";

interface CategoryTilesProps {
  tiles: CategoryTile[];
  pickImage: (keywords: string[]) => string | undefined;
  /** Background for a `shopAll` cutout tile. Falls back to the brand's own
   * primary color. */
  shopAllBackgroundColor?: string;
}

export const CategoryTiles: React.FC<CategoryTilesProps> = ({
  tiles,
  pickImage,
  shopAllBackgroundColor,
}) => {
  if (tiles.length === 0) return null;
  return (
    <div className={cn("pt-12 pb-10", PADX)}>
      {/* Desktop: one row, tiles flex to share the width up to the circle's own
          150px cap, then center as a group — so a brand with few categories
          (e.g. 5) packs tiles together instead of stretching them across the
          full row with oversized gutters around an undersized circle.
          Mobile: a swipeable row of fixed 72px circles (Faire's mweb pattern) so
          labels don't collide when there are many categories. */}
      <div className="flex gap-8 overflow-x-auto [scrollbar-width:none] md:justify-center md:overflow-x-visible [&::-webkit-scrollbar]:hidden">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            data-signup-gate
            className="flex w-[72px] shrink-0 flex-col items-center gap-2 md:w-auto md:max-w-[150px] md:min-w-0 md:flex-1"
          >
            {tile.shopAll ? (
              <div
                className="relative aspect-square w-full max-w-[150px] overflow-hidden rounded-full"
                style={{ backgroundColor: shopAllBackgroundColor }}
              >
                <img
                  src={tile.image}
                  alt={tile.label}
                  className="absolute inset-0 h-full w-full object-contain px-3 pt-3 pb-9"
                />
                <div className="absolute inset-x-0 bottom-0 flex justify-center pb-3">
                  <span
                    className="type-fs-paragraph-medium text-center"
                    style={{ color: "#ffffff" }}
                  >
                    {tile.label}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-fs-surface-secondary relative aspect-square w-full max-w-[150px] overflow-hidden rounded-full">
                {pickImage(tile.keywords) ? (
                  <>
                    <img
                      src={pickImage(tile.keywords)}
                      alt={tile.label}
                      className="h-full w-full object-cover"
                    />
                    {/* Same faint scrim as ProductCard — a little definition for
                        photos with pure white backgrounds against the page's own
                        white. */}
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{ backgroundColor: "rgba(0,0,0,0.025)" }}
                    />
                  </>
                ) : null}
              </div>
            )}
            {tile.shopAll ? null : (
              <span className="type-fs-paragraph text-fs-text-primary text-center">
                {tile.label}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
